import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import PixAndGoHeader from "@/components/molecules/PixAndGoHeader";
import PixAndGoLayout from "@/components/molecules/PixAndGoLayout";
import PixAndGoStepper from "@/components/molecules/PixAndGoStepper";
import { usePixAndGoConfig } from "@/context/PixAndGoContext";
import i18n from "@/translate/i18n";

const CURRENCY = "BRL";
const DEFAULT_PRESETS = [50, 100, 500];

function PixAndGoAmountContent() {
  const history = useHistory();
  const { merchantSlug } = useParams();
  const { tagline, presets, termsUrl, merchantLogoUrl } = usePixAndGoConfig();

  const presetAmounts = presets || DEFAULT_PRESETS;

  const [amount, setAmount] = useState("");
  const [error, setError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    i18n.changeLanguage("pt-BR");
  }, []);

  const handlePreset = (preset) => {
    setAmount(String(preset));
    setError(null);
  };

  const handleSubmit = () => {
    const parsed = parseFloat(amount);
    if (!amount || Number.isNaN(parsed) || parsed <= 0) {
      setError(i18n.t("pixAndGo.amount.amountInvalid"));
      return;
    }
    if (termsUrl && !termsAccepted) {
      setError(i18n.t("pixAndGo.amount.termsRequired"));
      return;
    }
    history.push({
      pathname: `/pix-and-go/${merchantSlug}/qr`,
      state: { amount: parsed, currency: CURRENCY },
    });
  };

  const selectedPreset = presetAmounts.includes(parseFloat(amount))
    ? parseFloat(amount)
    : null;

  return (
    <main className="flex flex-col items-center flex-1 px-6 pb-12">
      <div className="w-full max-w-sm flex flex-col gap-6">
        {tagline && (
          <p className="text-center text-2xl font-light text-pag-on-background leading-snug pt-4">
            {tagline}
          </p>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-end bg-pag-surface border-2 border-pag-primary rounded-lg px-6 h-20 gap-2">
            <span className="text-pag-on-surface text-2xl font-bold shrink-0">
              R$
            </span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError(null);
              }}
              placeholder="0"
              className="w-full outline-none text-3xl font-bold text-pag-on-surface bg-transparent text-right"
            />
          </div>

          <div className="flex gap-2.5">
            {presetAmounts.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => handlePreset(preset)}
                className={`flex-1 h-12 rounded-lg text-base font-bold transition-colors ${
                  selectedPreset === preset
                    ? "bg-pag-accent text-pag-on-primary border-2 border-pag-accent/50"
                    : "bg-pag-surface text-pag-on-surface"
                }`}
              >
                R${preset}
              </button>
            ))}
          </div>

          {error && <p className="text-sm text-pag-danger">{error}</p>}
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          className="w-full rounded-lg overflow-hidden flex flex-col border-2 border-pag-accent/40"
        >
          <div className="bg-pag-primary flex items-center justify-center py-6">
            <span className="text-pag-on-primary font-bold text-3xl">
              {i18n.t("pixAndGo.amount.submit")}
            </span>
          </div>
          <div className="bg-pag-accent flex items-center justify-center gap-3 py-3 px-6">
            <span className="text-pag-on-primary text-sm">
              {i18n.t("pixAndGo.amount.ctaSubtitle")}
            </span>
            {merchantLogoUrl && (
              <>
                <span className="text-pag-on-primary opacity-50">|</span>
                <img
                  src={merchantLogoUrl}
                  alt="merchant"
                  className="h-6 object-contain"
                />
              </>
            )}
          </div>
        </button>

        {termsUrl && (
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="pag-terms"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                setError(null);
              }}
              className="mt-0.5 w-4 h-4 shrink-0 cursor-pointer accent-pag-primary"
            />
            <label
              htmlFor="pag-terms"
              className="text-sm text-pag-on-background cursor-pointer leading-relaxed"
            >
              {i18n.t("pixAndGo.amount.termsPrefix")}{" "}
              <a
                href={termsUrl}
                target="_blank"
                rel="noreferrer"
                className="text-pag-accent underline"
                onClick={(e) => e.stopPropagation()}
              >
                {i18n.t("pixAndGo.amount.termsLink")}
              </a>
            </label>
          </div>
        )}
      </div>
    </main>
  );
}

function PixAndGoAmount() {
  const { merchantSlug } = useParams();

  return (
    <PixAndGoLayout merchantSlug={merchantSlug}>
      <PixAndGoHeader />
      <PixAndGoStepper currentStep={1} />
      <PixAndGoAmountContent />
    </PixAndGoLayout>
  );
}

export default PixAndGoAmount;
